import React, { useEffect, useState } from "react";
import { Input, Popover, PopoverContent, PopoverTrigger } from "@nextui-org/react";
import { IoChevronDown, IoTimeOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";

import { formatTimecode, parseTimecode } from "./MAudioPlayer/timecode";

// Page filter type understood by the backend (calls_router.get_widget_filter_string).
// Its params are ["min_seconds", "max_seconds"], "" leaving that side open.
export const CALL_DURATION_FILTER_TYPE = "call_duration";

const PRESETS = [
  { label: "< 30s", value: [null, 30] },
  { label: "30s - 2min", value: [30, 120] },
  { label: "2 - 5min", value: [120, 300] },
  { label: "> 5min", value: [300, null] },
];

const toBound = (param) =>
  param === "" || param === undefined || param === null ? null : Number(param);

// [min, max] in seconds (null = open) from a call_duration page filter.
export const getCallDurationRange = (pageFilters = []) => {
  const params = pageFilters.find((item) => item?.type === CALL_DURATION_FILTER_TYPE)?.params || [];
  return [toBound(params[0]), toBound(params[1])];
};

// Replaces the call_duration page filter, or drops it when the range is fully open.
export const withCallDurationRange = (pageFilters, [min, max]) => {
  const otherFilters = pageFilters.filter((item) => item?.type !== CALL_DURATION_FILTER_TYPE);

  if (min === null && max === null) {
    return otherFilters;
  }

  return [
    ...otherFilters,
    { type: CALL_DURATION_FILTER_TYPE, params: [min ?? "", max ?? ""].map(String) },
  ];
};

export const formatCallDurationRange = ([min, max]) => {
  if (min !== null && max !== null) return `${formatTimecode(min)} - ${formatTimecode(max)}`;
  if (min !== null) return `≥ ${formatTimecode(min)}`;
  if (max !== null) return `≤ ${formatTimecode(max)}`;
  return "";
};

const CallDurationDropdown = ({ value = [null, null], onChange = () => {} }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [minDraft, setMinDraft] = useState("");
  const [maxDraft, setMaxDraft] = useState("");

  const [min, max] = value;

  // Re-sync the inputs with the applied range whenever the popover opens, so a
  // range cleared from its chip does not reappear in the inputs.
  useEffect(() => {
    if (!isOpen) return;
    setMinDraft(min !== null ? formatTimecode(min) : "");
    setMaxDraft(max !== null ? formatTimecode(max) : "");
  }, [isOpen, min, max]);

  const parsedMin = minDraft.trim() ? parseTimecode(minDraft) : null;
  const parsedMax = maxDraft.trim() ? parseTimecode(maxDraft) : null;
  const isMinInvalid = !!minDraft.trim() && parsedMin === null;
  const isMaxInvalid = !!maxDraft.trim() && parsedMax === null;
  const isRangeInvalid = parsedMin !== null && parsedMax !== null && parsedMin > parsedMax;
  const canApply = !isMinInvalid && !isMaxInvalid && !isRangeInvalid;

  const apply = (range) => {
    onChange(range);
    setIsOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && canApply) {
      apply([parsedMin, parsedMax]);
    }
  };

  const isPresetSelected = (preset) => preset.value[0] === min && preset.value[1] === max;

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      triggerScaleOnOpen={false}
      isOpen={isOpen}
      onOpenChange={(open) => setIsOpen(open)}
    >
      <PopoverTrigger>
        <div className="hover:bg-black/10 duration-400 rounded-md px-[12px] py-[8px] flex items-center cursor-pointer gap-[8px]">
          <IoTimeOutline size={16} />
          <span className="whitespace-nowrap leading-3 text-[14px]">{t("Call duration")}</span>
          <IoChevronDown size={16} />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-4">
        <div className="w-full flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            {PRESETS.map((preset) => (
              <div
                key={preset.label}
                onClick={() => apply(preset.value)}
                onKeyDown={() => apply(preset.value)}
                className={`py-1 rounded-md text-center text-sm cursor-pointer border-1 ${
                  isPresetSelected(preset)
                    ? "bg-[#4318ff]/[0.18] text-primary border-transparent"
                    : "border-[#E5E5E5] text-[#404040] hover:bg-[#F5F5F5]"
                }`}
              >
                {preset.label}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              size="sm"
              label={t("Min")}
              placeholder="m:ss"
              value={minDraft}
              onValueChange={setMinDraft}
              onKeyDown={onKeyDown}
              isInvalid={isMinInvalid || isRangeInvalid}
            />
            <Input
              size="sm"
              label={t("Max")}
              placeholder="m:ss"
              value={maxDraft}
              onValueChange={setMaxDraft}
              onKeyDown={onKeyDown}
              isInvalid={isMaxInvalid || isRangeInvalid}
            />
          </div>
          <span className="text-xs text-[#737373]">
            {t("Format m:ss, a single number is a number of minutes")}
          </span>
          <div className="flex justify-between gap-2">
            <div
              onClick={() => apply([null, null])}
              onKeyDown={() => apply([null, null])}
              className="px-[12px] py-[6px] rounded-md text-sm cursor-pointer text-[#737373] hover:bg-[#F5F5F5]"
            >
              {t("Reset")}
            </div>
            <div
              onClick={() => canApply && apply([parsedMin, parsedMax])}
              onKeyDown={() => canApply && apply([parsedMin, parsedMax])}
              className={`px-[12px] py-[6px] rounded-md text-sm text-white bg-primary ${
                canApply ? "cursor-pointer" : "cursor-not-allowed opacity-50"
              }`}
            >
              {t("Apply")}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CallDurationDropdown;
